import {Fragment, useContext, useEffect, useState} from 'react'

import cx from 'classnames'
import PropTypes from 'prop-types'

import AtomBadge, {atomBadgeDesigns, atomBadgeSizes} from '@s-ui/react-atom-badge'
import AtomButton, {atomButtonDesigns, atomButtonShapes, atomButtonSizes} from '@s-ui/react-atom-button'
import {ATOM_ICON_SIZES, atomIconSizes} from '@s-ui/react-atom-icon'
import AtomRadioButton from '@s-ui/react-atom-radio-button'
import Context from '@s-ui/react-context'
import MoleculeAccordion, {moleculeAccordionBehavior, MoleculeAccordionItem} from '@s-ui/react-molecule-accordion'
import Layout from '@adv-mt/mt-layout-basic'
import {useMediaQuery, useMediaQueryBreakpoints} from '@adv-mt/mt-layout-render-media-query'
import PanelBasic from '@adv-mt/mt-panel-basic'
import ArrowRight from '@adv-mt/mt-svg-icons/lib/ArrowRight'
import ChevronDown from '@adv-mt/mt-svg-icons/lib/ChevronDown'
import ChevronUp from '@adv-mt/mt-svg-icons/lib/ChevronUp'
import Close from '@adv-mt/mt-svg-icons/lib/Close'
import RocketOuline from '@adv-mt/mt-svg-icons/lib/RocketOutline'
import TitleBasic, {titleBasicSizes, titleBasicTags} from '@adv-mt/mt-title-basic'

import SaleTypeComparisonDrawer from './components/SaleTypeComparisonDrawer/index.js'

const BASE_CLASS_NAME = 'mt-PublishMyAdWithPlate-stepSaleTypeSelection'

const SALE_TYPE = {
  hybrid: 'hybrid',
  c2b: 'c2b',
  publish: 'publish'
}

const StepSaleTypeSelection = ({instantOffer, km, onSelectVehicle, selectedVehicle, userData}) => {
  const {domain, i18n} = useContext(Context)
  const config = domain.get('config')
  const isMobile = config.get('isMobile')
  const {PUBLISH_MY_AD_PATH_PATTERN} = config.get('page')

  const isWiderThanMobile = useMediaQuery(useMediaQueryBreakpoints.md, {defaultMatches: !isMobile})

  const [isLoading, setIsLoading] = useState(false)
  const [selectedOption, setSelectedOption] = useState(null)
  const [showError, setShowError] = useState(false)
  const [showComparison, setShowComparison] = useState(false)
  const [recommendedPrice, setRecommendedPrice] = useState(null)

  const {appraisal, vehicleData} = instantOffer
  const {
    make,
    makeId,
    model,
    modelId,
    fuelType,
    fuelTypeId,
    plateRegistrationDate,
    plateRegistrationYear,
    transmissionTypeId,
    versionId,
    version,
    bodyTypeId,
    doors
  } = selectedVehicle || {}

  const plate = vehicleData?.licensePlate
  const postalCode = userData?.postalCode

  useEffect(() => {
    if (!selectedVehicle) return

    domain
      .get('get_recommended_price_seller_use_case')
      .execute({
        bodyTypeDoors: `${bodyTypeId}-${doors}`,
        fuelId: fuelTypeId,
        makeId,
        km,
        modelId,
        transmissionTypeId,
        versionId,
        year: plateRegistrationYear
      })
      .then(([error, response]) => {
        if (!error && response?.roundedRecommendPrice) {
          setRecommendedPrice(response.roundedRecommendPrice)
        }
      })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelectOption = option => {
    setSelectedOption(option)
    if (showError) setShowError(false)
  }

  const handleContinue = async () => {
    if (!selectedOption) {
      setShowError(true)
      return
    }

    if (selectedOption === SALE_TYPE.hybrid || selectedOption === SALE_TYPE.publish) {
      setIsLoading(true)

      const [draftError, draftResponse] = await domain.get('obtain_draft_ad_publisher_use_case').execute()

      if (draftError) {
        setIsLoading(false)
        return
      }

      const {
        data: {id: adId}
      } = draftResponse

      const fields = {
        bodyTypeDoors: `${bodyTypeId}-${doors}`,
        carPlate: plate,
        fuelId: fuelTypeId,
        km,
        makeId,
        modelId,
        plateRegistrationDate,
        publishInMA: true,
        transmissionTypeId,
        versionId,
        year: plateRegistrationYear
      }

      await domain.get('edit_draft_ad_publisher_use_case').execute({adId, fields})

      onSelectVehicle({
        adId,
        destinationUrl: `${PUBLISH_MY_AD_PATH_PATTERN}?withPlate=true&withPlateStatus=plateRecommendedPrice`,
        fields
      })
    }
  }

  const saleTypeOptions = [
    {
      id: SALE_TYPE.hybrid,
      badge: {
        design: atomBadgeDesigns.SOLID,
        label: i18n.t('PUBLISH_AD.SALE_TYPE_SELECTION.OPTION_HYBRID_BADGE')
      },
      title: i18n.t('PUBLISH_AD.SALE_TYPE_SELECTION.OPTION_HYBRID_TITLE'),
      description: i18n.t('PUBLISH_AD.SALE_TYPE_SELECTION.OPTION_HYBRID_DESCRIPTION'),
      highlight: i18n.t('PUBLISH_AD.SALE_TYPE_SELECTION.OPTION_HYBRID_HIGHLIGHT')
    },
    {
      id: SALE_TYPE.c2b,
      badge: {
        design: atomBadgeDesigns.SOFT,
        icon: <RocketOuline />,
        label: i18n.t('PUBLISH_AD.SALE_TYPE_SELECTION.OPTION_C2B_BADGE'),
        type: 'prosLight'
      },
      title: i18n.t('PUBLISH_AD.SALE_TYPE_SELECTION.OPTION_C2B_TITLE'),
      description: i18n.t('PUBLISH_AD.SALE_TYPE_SELECTION.OPTION_C2B_DESCRIPTION'),
      highlight: i18n.t('PUBLISH_AD.SALE_TYPE_SELECTION.OPTION_C2B_HIGHLIGHT'),
      price: i18n.c(appraisal.priceRecommended.value)
    },
    {
      id: SALE_TYPE.publish,
      title: i18n.t('PUBLISH_AD.SALE_TYPE_SELECTION.OPTION_PUBLISH_TITLE'),
      description: i18n.t('PUBLISH_AD.SALE_TYPE_SELECTION.OPTION_PUBLISH_DESCRIPTION'),
      highlight: recommendedPrice !== null ? i18n.t('PUBLISH_AD.SALE_TYPE_SELECTION.OPTION_PUBLISH_HIGHLIGHT') : null,
      price: recommendedPrice !== null ? i18n.c(recommendedPrice) : null,
      showPriceSkeleton: recommendedPrice === null
    }
  ]

  const vehicleTitle = `${make} ${model} ${version}`.trim()
  const vehicleSpecItems = [
    fuelType && {value: fuelType},
    plateRegistrationYear && {value: String(plateRegistrationYear)},
    km && {value: `${Number(km).toLocaleString(i18n.culture)} km`}
  ].filter(Boolean)

  const vehicleDetailsContent = (
    <div
      className={cx(`${BASE_CLASS_NAME}-vehicleDetails`, {
        [`${BASE_CLASS_NAME}-vehicleDetails--withPadding8`]: !isWiderThanMobile
      })}
    >
      {vehicleSpecItems.length > 0 && (
        <div className={`${BASE_CLASS_NAME}-vehicleSpecs`}>
          {vehicleSpecItems.map(({value}, index) => (
            <Fragment key={value}>
              {index > 0 && <span aria-hidden="true">·</span>}
              <span>{value}</span>
            </Fragment>
          ))}
        </div>
      )}
      <div className={`${BASE_CLASS_NAME}-vehicleLocation`}>
        <span>{`CP ${postalCode}`}</span>
        <span aria-hidden="true">·</span>
        <span className={`${BASE_CLASS_NAME}-vehiclePlate`}>{plate}</span>
      </div>
    </div>
  )

  return (
    <Layout.Container isFullWidth>
      <div className={BASE_CLASS_NAME}>
        {selectedVehicle && (
          <div className={`${BASE_CLASS_NAME}-vehicleHeader`}>
            {isWiderThanMobile ? (
              <PanelBasic elevation="l" rounded="l" withBorder={false}>
                <h2 className={`${BASE_CLASS_NAME}-vehicleTitle`}>{vehicleTitle}</h2>
                {vehicleDetailsContent}
              </PanelBasic>
            ) : (
              <MoleculeAccordion
                as={Fragment}
                behavior={moleculeAccordionBehavior.SINGLE}
                headerIconCollapsed={<ChevronDown size={ATOM_ICON_SIZES.medium} />}
                headerIconExpanded={<ChevronUp size={ATOM_ICON_SIZES.medium} />}
              >
                <MoleculeAccordionItem headerAs="h2" content={vehicleDetailsContent} label={vehicleTitle} value={0} />
              </MoleculeAccordion>
            )}
          </div>
        )}

        <div className={`${BASE_CLASS_NAME}-main`}>
          <TitleBasic
            as={titleBasicTags.h2}
            size={titleBasicSizes.s}
            title={i18n.t('PUBLISH_AD.SALE_TYPE_SELECTION.TITLE')}
          />

          <ul className={`${BASE_CLASS_NAME}-options`}>
            {saleTypeOptions.map(({badge, description, highlight, id, price, showPriceSkeleton, title}) => (
              <li
                key={id}
                className={`${BASE_CLASS_NAME}-option ${
                  selectedOption === id ? `${BASE_CLASS_NAME}-option--selected` : ''
                }`}
                onClick={() => handleSelectOption(id)}
              >
                <div className={`${BASE_CLASS_NAME}-optionHeader`}>
                  <AtomRadioButton
                    id={id}
                    name="saleType"
                    checked={selectedOption === id}
                    onChange={() => handleSelectOption(id)}
                  />
                  {badge && (
                    <AtomBadge
                      design={badge.design}
                      icon={badge.icon}
                      label={badge.label}
                      size={atomBadgeSizes.LARGE}
                      type={badge.type}
                    />
                  )}
                </div>
                <div className={`${BASE_CLASS_NAME}-optionContent`}>
                  <TitleBasic as={titleBasicTags.h3} size={titleBasicSizes.xs} title={title} />
                  <p className={`${BASE_CLASS_NAME}-optionDescription`}>{description}</p>
                  {highlight && <p className={`${BASE_CLASS_NAME}-optionHighlight`}>{highlight}</p>}
                  {price && <strong className={`${BASE_CLASS_NAME}-optionPrice`}>{price}*</strong>}
                  {showPriceSkeleton && (
                    <span className={`${BASE_CLASS_NAME}-optionPriceSkeleton`} aria-hidden="true" />
                  )}
                </div>
              </li>
            ))}
          </ul>

          {showError && (
            <p className={`${BASE_CLASS_NAME}-error`} role="alert">
              <Close size={atomIconSizes.medium} /> {i18n.t('PUBLISH_AD.SALE_TYPE_SELECTION.NO_SELECTION_ERROR')}
            </p>
          )}

          <div className={`${BASE_CLASS_NAME}-ctaGroup`}>
            <AtomButton
              disabled={isLoading}
              fullWidth={!isWiderThanMobile}
              isLoading={isLoading}
              onClick={handleContinue}
              shape={atomButtonShapes.CIRCULAR}
              size={atomButtonSizes.LARGE}
              rightIcon={<ArrowRight size={ATOM_ICON_SIZES.medium} />}
            >
              {isWiderThanMobile ? 'Continuar con la venta' : i18n.t('COMMON.CONTINUE')}
            </AtomButton>

            <AtomButton
              design={atomButtonDesigns.LINK}
              fullWidth={!isWiderThanMobile}
              onClick={() => setShowComparison(true)}
            >
              {i18n.t('PUBLISH_AD.SALE_TYPE_SELECTION.COMPARE_OPTIONS_LINK')}
            </AtomButton>
          </div>

          <p className={`${BASE_CLASS_NAME}-disclaimer`}>{i18n.t('PUBLISH_AD.SALE_TYPE_SELECTION.PRICE_DISCLAIMER')}</p>
        </div>

        <SaleTypeComparisonDrawer
          isOpen={showComparison}
          isWiderThanMobile={isWiderThanMobile}
          onClose={() => setShowComparison(false)}
        />
      </div>
    </Layout.Container>
  )
}

StepSaleTypeSelection.displayName = 'StepSaleTypeSelection'

StepSaleTypeSelection.propTypes = {
  instantOffer: PropTypes.shape({
    appraisal: PropTypes.shape({
      priceRecommended: PropTypes.shape({
        value: PropTypes.number.isRequired
      }).isRequired
    }).isRequired,
    vehicleData: PropTypes.shape({
      licensePlate: PropTypes.string
    })
  }).isRequired,
  km: PropTypes.string,
  onSelectVehicle: PropTypes.func.isRequired,
  selectedVehicle: PropTypes.shape({
    bodyTypeId: PropTypes.string,
    doors: PropTypes.string,
    fuelType: PropTypes.string,
    fuelTypeId: PropTypes.string,
    make: PropTypes.string,
    makeId: PropTypes.string,
    model: PropTypes.string,
    modelId: PropTypes.string,
    plateRegistrationDate: PropTypes.string,
    plateRegistrationYear: PropTypes.string,
    transmissionTypeId: PropTypes.string,
    versionId: PropTypes.string
  }),
  userData: PropTypes.shape({
    postalCode: PropTypes.string
  })
}

export default StepSaleTypeSelection
